import styled from 'styled-components'

import { media } from '@/theme/common'

import { ModalSizeKeys } from './modal.types'

const MODAL_SIZE_MAP: Record<ModalSizeKeys, string> = {
  large: '90rem',
  medium: '60rem',
  small: '50rem',
}

export const ModalContent = styled.div<{
  $size: ModalSizeKeys
  $height?: string
  $width?: string
  $autoWidth?: boolean
}>`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.border.radius['4']};
  box-shadow: ${({ theme }) => theme.shadow.xl};
  display: flex;
  flex-direction: column;
  height: ${({ $height }) => $height ?? 'auto'};
  max-height: ${({ $height }) => ($height ? 'none' : '75vh')};
  max-width: ${({ $size, $width, $autoWidth }) =>
    $autoWidth ? '90vw' : ($width ?? MODAL_SIZE_MAP[$size])};
  outline: none;
  width: ${({ $autoWidth }) => ($autoWidth ? 'auto' : '100%')};

  // Note: full-screen sheet on mobile, positioned fixed rather than relying on percentage
  // height - ContentWrapper (overlay.styled.ts) only sizes to its content along the main
  // axis, so a plain height: 100% here would resolve against an indefinite container and
  // collapse back to auto (see date-picker-input.styled.ts's FloatingWrapper for the same
  // fixed-positioning fix applied to a popover). ModalHeader/ModalFooter stay pinned
  // (flex-shrink: 0) and only ModalBody (flex: 1, overflow: auto) scrolls.
  ${media.down('sm')} {
    border-radius: 0;
    bottom: 0;
    box-shadow: none;
    left: 0;
    max-height: none;
    max-width: none;
    position: fixed;
    right: 0;
    top: 0;
  }
`

export const ModalHeader = styled.div(({ theme }) => ({
  alignItems: 'center',
  borderBottom: `${theme.border.size['1']} solid ${theme.colors.gray500}`,
  display: 'flex',
  flexShrink: 0,
  justifyContent: 'space-between',
  padding: `${theme.space['16']} ${theme.space['24']}`,
}))

export const ModalHeaderTitle = styled.div(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: theme.space['8'],
}))

export const ModalHeaderIcon = styled.div(({ theme }) => ({
  alignItems: 'center',
  color: theme.colors.black600,
  display: 'flex',
}))

export const ModalCloseButton = styled.button(({ theme }) => ({
  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.4,
  },

  '&:focus-visible': {
    outline: `2px solid ${theme.colors.blue500}`,
    outlineOffset: '2px',
  },

  '&:hover:not(:disabled)': {
    color: theme.colors.black800,
  },

  alignItems: 'center',
  background: 'transparent',
  border: 'none',
  borderRadius: theme.border.radius['4'],
  color: theme.colors.black600,
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'center',
  transition: `color ${theme.transition.duration.sm}`,
}))

export const ModalBody = styled.div<{ $paddingTop?: string }>(({ theme, $paddingTop }) => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  overflow: 'auto',
}))

export const ModalBodyContent = styled.div<{ $paddingTop?: string }>(({ theme, $paddingTop }) => ({
  padding: `${$paddingTop ?? theme.space['24']} ${theme.space['24']} ${theme.space['24']} ${theme.space['24']}`,
}))

export const ModalFooter = styled.div(({ theme }) => ({
  borderTop: `${theme.border.size['1']} solid ${theme.colors.gray500}`,
  display: 'flex',
  flexShrink: 0,
  gap: theme.space['12'],
  justifyContent: 'flex-end',
  padding: `${theme.space['16']} ${theme.space['24']}`,
}))
